/**
 * Money is ALWAYS an integer number of paise. Never use floats for money: no `/ 100` on the way
 * in, no `* 100` on user input. Arithmetic uses BigInt so very large totals stay exact; the
 * backend (uc-core common/utils/money.ts) is the authority and rounds once, half-up.
 */

export type Paise = number

const INR_GROUPING = new Intl.NumberFormat('en-IN', { useGrouping: true, maximumFractionDigits: 0 })

function toBigInt(value: Paise | bigint): bigint {
  if (typeof value === 'bigint') return value
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`Money must be an integer number of paise, got ${value}`)
  }
  return BigInt(value)
}

export interface FormatMoneyOptions {
  /** Hide ".00" when the amount is whole rupees. Default false (accounting style). */
  trimZeroPaise?: boolean
  /** Include the ₹ symbol. Default true. */
  symbol?: boolean
}

/** 12345678 → "₹1,23,456.78" (Indian digit grouping). */
export function formatPaise(value: Paise | bigint, opts: FormatMoneyOptions = {}): string {
  const { trimZeroPaise = false, symbol = true } = opts
  const p = toBigInt(value)
  const negative = p < 0n
  const abs = negative ? -p : p
  const rupees = abs / 100n
  const paise = abs % 100n
  const rupeeText = INR_GROUPING.format(rupees)
  const paiseText = paise.toString().padStart(2, '0')
  const body = trimZeroPaise && paise === 0n ? rupeeText : `${rupeeText}.${paiseText}`
  return `${negative ? '−' : ''}${symbol ? '₹' : ''}${body}`
}

/**
 * Parse a rupee amount typed by a person ("1,234.5", "₹ 99") into paise without floats.
 * Returns null for anything that is not a valid amount with at most 2 decimals.
 */
export function parseRupeesToPaise(input: string): Paise | null {
  const cleaned = input.replace(/[₹,\s]/g, '')
  const m = /^(-)?(\d+)(?:\.(\d{0,2}))?$/.exec(cleaned)
  if (!m) return null
  const [, sign, whole = '0', frac = ''] = m
  const p = BigInt(whole) * 100n + BigInt(frac.padEnd(2, '0') || '0')
  const signed = sign ? -p : p
  if (signed > BigInt(Number.MAX_SAFE_INTEGER) || signed < BigInt(Number.MIN_SAFE_INTEGER)) {
    return null
  }
  return Number(signed)
}

/** Integer division rounding half away from zero — the single rounding rule. */
export function divRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new RangeError('Division by zero')
  const negative = numerator < 0n !== denominator < 0n
  const n = numerator < 0n ? -numerator : numerator
  const d = denominator < 0n ? -denominator : denominator
  const q = (n * 2n + d) / (d * 2n)
  return negative ? -q : q
}

/**
 * Value of a weight at a per-kg rate: grams × paise/kg ÷ 1000, rounded half-up once.
 * For DISPLAY on the phone only — the server computes the amount that is paid.
 */
export function valueOfWeight(grams: number, paisePerKg: Paise): Paise {
  const v = divRoundHalfUp(toBigInt(grams) * toBigInt(paisePerKg), 1000n)
  return Number(v)
}

/** Sum paise exactly. */
export function sumPaise(values: ReadonlyArray<Paise>): Paise {
  return Number(values.reduce<bigint>((acc, v) => acc + toBigInt(v), 0n))
}
